"use client";
import Link from "next/link";
import axios from "@config/axios";
import { languages } from "@data";
import { AxiosError } from "axios";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { useLayoutEffect, useState } from "react";
import { Combobox } from "@components/ui/combobox";
import CatalogueOfferings from "./catalogue-offerings";
import { useDebounce, useWindowSize } from "react-use";
import { QueryKey, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ListRestart, Search } from "lucide-react";

type CataloguesWrapperProps = {
	cataloguesData: Catalogue[];
	totalPages: number;
};

const CataloguesWrapper = ({ cataloguesData, totalPages }: CataloguesWrapperProps) => {
	const { width } = useWindowSize();
	const [filter, setFilter] = useState<string>("");
	const [language, setLanguage] = useState<string>("");
	const [pageIndex, setPageIndex] = useState<number>(0);
	const [mounted, setMounted] = useState<boolean>(false);
	const [pageCount, setPageCount] = useState<number>(totalPages);
	const [globalFilter, setGlobalFilter] = useState<string>(filter);
	const [offeringsLength, setOfferingsLength] = useState<number>(0);
	useDebounce(() => setGlobalFilter(filter), 500, [filter]);

	const getAllCatalogues = async ({ queryKey }: { queryKey: QueryKey }) => {
		const [, search, language, page] = queryKey;
		try {
			const response = await axios.get(`/api/course/get-all-course-catalogue?size=3${page ? `&page=${page}` : ""}${search ? `&search=${search}` : ""}${language ? `&language=${language}` : ""}`);
			setPageCount(response?.data?.totalPages);
			return response?.data?.content as Catalogue[];
		} catch (error: unknown) {
			console.info((error as AxiosError<string>)?.response?.data || "Internal server error!");
			return [];
		}
	};

	const { data: catalogues } = useQuery<Catalogue[]>({
		queryKey: ["catalogues", globalFilter, language, pageIndex],
		queryFn: getAllCatalogues,
		initialData: cataloguesData,
		retry: 0,
	});

	const resetFilters = () => {
		setFilter("");
		setGlobalFilter("");
		setLanguage("");
	};

	useLayoutEffect(() => setMounted(true), []);

	useLayoutEffect(() => {
		if (width < 640) {
			setOfferingsLength(1);
		} else if (width < 1024) {
			setOfferingsLength(2);
		} else {
			setOfferingsLength(3);
		}
	}, [width]);

	if (mounted) {
		return (
			<>
				<div className="flex w-full flex-col items-center justify-end gap-1 py-4 xl:flex-row">
					<Input className="max-w-sm xl:max-w-64" name="search-input" onChange={event => setFilter(event.target.value)} placeholder="Search Course" startContent={<Search size={16} />} value={filter} />
					<Combobox className="max-w-sm xl:max-w-32" onChange={setLanguage} options={languages} placeholder="Language" value={language} />
					<div className="flex w-full flex-wrap items-center justify-center gap-1 xl:w-auto">
						<Button className="text-xs" onClick={resetFilters} size="lg" type="button" variant="outline">
							<ListRestart size={16} />
							Reset
						</Button>
					</div>
				</div>
				<div className="flex w-full flex-1 justify-center gap-2">
					{catalogues?.length === 0 ? (
						<div className="flex h-full w-full flex-col items-center justify-center gap-5">
							<p className="text-destructive text-center">No courses found!</p>
						</div>
					) : (
						<div className="flex w-full flex-col items-center">
							{catalogues?.map(catalogue => (
								<div className="flex w-full flex-col items-center justify-center gap-2 not-last:border-b not-last:pb-4" key={catalogue?.uuid}>
									<div className="flex w-full items-center justify-between gap-2 p-2">
										<h1 className="truncate text-lg font-medium">
											{catalogue?.offeredBy === "ORGANIZATION" ? catalogue?.creator?.name : catalogue?.offeredBy === "INDIVIDUAL" ? `${catalogue?.creator?.title} ${catalogue?.creator?.firstName} ${catalogue?.creator?.middleName} ${catalogue?.creator?.lastName}` : "Recommended Courses"}
										</h1>
										{catalogue?.courses?.length > offeringsLength && (
											<Link className="shrink-0 text-sm font-light" href={`/offerings/${catalogue?.uuid}`}>
												View All
											</Link>
										)}
									</div>
									<CatalogueOfferings courses={catalogue?.courses} key={catalogue?.uuid} />
								</div>
							))}
						</div>
					)}
				</div>
				<div className="xs:flex-row xs:justify-between flex w-full flex-col items-center justify-center gap-2 p-2">
					{`Page ${pageIndex + 1} of ${pageCount || 1}`}
					<div className="flex items-center justify-center space-x-2">
						<Button disabled={pageIndex === 0} onClick={() => setPageIndex(prevPageIndex => prevPageIndex - 1)} variant="ghost">
							<ChevronLeft size={16} />
							Prev
						</Button>
						<Button disabled={pageCount === 0 || pageIndex === pageCount - 1} onClick={() => setPageIndex(prevPageIndex => prevPageIndex + 1)} variant="ghost">
							Next
							<ChevronRight size={16} />
						</Button>
					</div>
				</div>
			</>
		);
	}
};

export default CataloguesWrapper;
